import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard,
  BookOpen,
  Users,
  Volume2,
  SlidersHorizontal,
  Headphones,
  Layers,
  Download,
  Radio,
  Settings as SettingsIcon,
  Gem,
  LogOut,
  Lock,
  Circle,
} from "lucide-react";

const MODULES = [
  { to: "/studio/projects", label: "Projects", icon: LayoutDashboard, active: true },
  { to: "/studio/manuscript", label: "Manuscript", icon: BookOpen },
  { to: "/studio/characters", label: "Character Studio", icon: Users },
  { to: "/studio/pronunciations", label: "Pronunciations", icon: Volume2 },
  { to: "/studio/production", label: "Production Desk", icon: SlidersHorizontal },
  { to: "/studio/review", label: "Take Reviewer", icon: Headphones },
  { to: "/studio/assembly", label: "Master Assembly", icon: Layers },
  { to: "/studio/exports", label: "Exports & Delivery", icon: Download },
  { to: "/studio/studio-sync", label: "Studio Sync", icon: Radio },
  { to: "/studio/beta", label: "Beta Preview", icon: Headphones },
];

export function AppShell() {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const [provider, setProvider] = useState(null);

  useEffect(() => {
    api.get("/settings/provider").then((r) => setProvider(r.data)).catch(() => setProvider(null));
  }, []);

  const connected = provider?.configured && provider?.last_test_status === "ok";

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="rs-grain flex min-h-screen bg-background text-foreground">
      <aside className="fixed inset-y-0 left-0 z-20 flex w-64 flex-col border-r border-border bg-[#090b0e]">
        <div className="flex items-center gap-3 border-b border-border px-5 py-5">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-amber-500/15 ring-1 ring-amber-500/30">
            <Gem className="h-5 w-5 text-amber-400" />
          </div>
          <div className="leading-tight">
            <div className="font-serif text-lg tracking-tight text-slate-100">Rik&apos;s Audiobook Studio</div>
          </div>
        </div>

        <nav className="flex-1 space-y-0.5 overflow-y-auto px-3 py-4">
          <div className="px-2 pb-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Workstation</div>
          {MODULES.map((m) => (
            <NavLink
              key={m.to}
              to={m.to}
              data-testid={`nav-${m.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `group flex items-center justify-between rounded-md px-3 py-2 text-sm transition-colors duration-200 ${
                  isActive
                    ? "bg-amber-500/10 text-amber-200 ring-1 ring-amber-500/20"
                    : "text-slate-400 hover:bg-amber-500/5 hover:text-amber-100"
                }`
              }
            >
              <span className="flex items-center gap-3">
                <m.icon className="h-4 w-4" />
                {m.label}
              </span>
              {m.phase && (
                <span className="flex items-center gap-1 font-mono text-[9px] uppercase tracking-wider text-slate-600">
                  <Lock className="h-3 w-3" />
                  {m.phase}
                </span>
              )}
            </NavLink>
          ))}

          <div className="px-2 pb-2 pt-5 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">System</div>
          <NavLink
            to="/studio/settings"
            data-testid="nav-settings"
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors duration-200 ${
                isActive
                  ? "bg-amber-500/10 text-amber-200 ring-1 ring-amber-500/20"
                  : "text-slate-400 hover:bg-amber-500/5 hover:text-amber-100"
              }`
            }
          >
            <SettingsIcon className="h-4 w-4" />
            Settings
          </NavLink>
        </nav>

        <div className="border-t border-border px-4 py-3">
          <div
            data-testid="sidebar-connection-status"
            className="mb-3 flex items-center gap-2 rounded-md bg-slate-900/60 px-3 py-2 text-xs"
          >
            <Circle
              className={`h-2.5 w-2.5 ${connected ? "fill-emerald-400 text-emerald-400" : "fill-slate-600 text-slate-600"}`}
            />
            <span className="text-slate-400">
              ElevenLabs{" "}
              <span className={connected ? "text-emerald-400" : "text-slate-500"}>
                {connected ? "connected" : "not connected"}
              </span>
            </span>
          </div>
          <div className="mb-2 truncate font-mono text-[11px] text-slate-500">{user?.email}</div>
          <Button
            data-testid="logout-button"
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className="w-full justify-start gap-2 text-slate-400 hover:bg-red-500/10 hover:text-red-300"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </Button>
        </div>
      </aside>

      <main className="relative z-10 ml-64 flex-1">
        <motion.div
          key={window.location.pathname}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          className="mx-auto max-w-6xl px-8 py-10"
        >
          <Outlet />
        </motion.div>
      </main>
    </div>
  );
}
